// Workshop Options service for KATA Dashboard

import { apiClient, handleApiError } from './api';
import { AxiosError } from 'axios';

// Types for workshop dropdown options
export interface WorkshopDropdownOption {
  id: number;
  attribute_name: string;
  value: string;
  label: string;
  code?: string;
  display_order: number;
  is_active: boolean;
  mapped_plant?: {
    id: number;
    name: string;
    code: string;
    alv_id?: string | null;
  } | null;
  has_layout?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  created_by_name?: string;
}

export interface WorkshopDropdownOptionCreate {
  attribute_name: string;
  value: string;
  label: string;
  code?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface WorkshopDropdownOptionUpdate {
  value?: string;
  label?: string;
  code?: string;
  display_order?: number;
  is_active?: boolean;
}

export interface BulkUpdateOption {
  id: number;
  display_order?: number;
  is_active?: boolean;
}

export interface BulkUpdateRequest {
  options: BulkUpdateOption[];
}

export interface AttributeType {
  value: string;
  label: string;
}

export interface GroupedOptions {
  [attributeName: string]: WorkshopDropdownOption[];
}

export interface PlantProvisionOption {
  id: number;
  name: string;
  code: string;
  location?: string;
  country?: string;
  alv_id?: string | null;
}

export interface ProvisionAlvPlantPayload {
  alv_id: string;
  mode: 'existing' | 'new';
  existing_plant_id?: number;
  plant_name?: string;
  plant_code?: string;
  location?: string;
  country?: string;
  layout_name?: string;
  width?: number;
  height?: number;
  replace_existing_layout?: boolean;
  force_reassign_alv?: boolean;
  background_image_file?: File | null;
}

export interface ProvisionAlvPlantResult {
  message: string;
  alv_id: string;
  plant: {
    id: number;
    name: string;
    code: string;
    alv_id: string | null;
  };
  layout: {
    id: number;
    name: string;
    background_image: string;
    created: boolean;
    replaced: boolean;
  };
  workshops_remapped: number;
}

export interface DepartmentManagementOption {
  id: number;
  name: string;
  code: string;
  description?: string;
  is_active: boolean;
  plant: number | null;
  plant_name?: string | null;
  plants_ids: number[];
  linked_plants: Array<{
    id: number;
    name: string;
    code: string;
    location?: string;
    country?: string;
    alv_id?: string | null;
  }>;
  workshop_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface DepartmentManagementPayload {
  name: string;
  code: string;
  description?: string;
  is_active?: boolean;
  plant?: number | null;
  plants?: number[];
}

/**
 * Get all workshop dropdown options
 * @param params Optional query parameters (attribute_name, is_active)
 */
export const getAllOptions = async (params?: {
  attribute_name?: string;
  is_active?: boolean;
}): Promise<WorkshopDropdownOption[]> => {
  try {
    const response = await apiClient.get('/workshop-options/', { params });
    return response.data.results || response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Get options for a specific attribute
 * @param attributeName The attribute name (e.g., 'status', 'priority')
 * @param activeOnly Whether to return only active options (default: false for admin panel)
 */
export const getOptionsByAttribute = async (
  attributeName: string,
  activeOnly: boolean = false
): Promise<WorkshopDropdownOption[]> => {
  try {
    const params = activeOnly ? { active_only: 'true' } : { active_only: 'false' };
    const response = await apiClient.get(`/workshop-options/by-attribute/${attributeName}/`, { params });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Get all options grouped by attribute name
 * Returns an object with attribute names as keys and arrays of options as values
 */
export const getGroupedOptions = async (): Promise<GroupedOptions> => {
  try {
    const response = await apiClient.get('/workshop-options/grouped/');
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Get list of all available attribute types
 */
export const getAttributes = async (): Promise<AttributeType[]> => {
  try {
    const response = await apiClient.get('/workshop-options/attributes/');
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Get a single option by ID
 * @param id Option ID
 */
export const getOption = async (id: number): Promise<WorkshopDropdownOption> => {
  try {
    const response = await apiClient.get(`/workshop-options/${id}/`);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Create a new dropdown option
 * @param data Option data
 */
export const createOption = async (
  data: WorkshopDropdownOptionCreate
): Promise<WorkshopDropdownOption> => {
  try {
    const response = await apiClient.post('/workshop-options/', data);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Update an existing dropdown option
 * @param id Option ID
 * @param data Updated option data
 */
export const updateOption = async (
  id: number,
  data: WorkshopDropdownOptionUpdate
): Promise<WorkshopDropdownOption> => {
  try {
    const response = await apiClient.patch(`/workshop-options/${id}/`, data);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Delete a dropdown option
 * @param id Option ID
 */
export const deleteOption = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/workshop-options/${id}/`);
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Bulk update multiple options (for reordering or activation)
 * @param options Array of options with id and fields to update
 */
export const bulkUpdateOptions = async (
  options: BulkUpdateOption[]
): Promise<{ message: string; updated_ids: number[] }> => {
  try {
    const response = await apiClient.post('/workshop-options/bulk-update/', {
      options,
    });
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Toggle the active status of an option
 * @param id Option ID
 * @param isActive New active status
 */
export const toggleOptionActive = async (
  id: number,
  isActive: boolean
): Promise<WorkshopDropdownOption> => {
  return updateOption(id, { is_active: isActive });
};

/**
 * Reorder options for a specific attribute
 * @param attributeName Attribute name
 * @param orderedIds Array of option IDs in desired order
 */
export const reorderOptions = async (
  attributeName: string,
  orderedIds: number[]
): Promise<{ message: string; updated_ids: number[] }> => {
  const options = orderedIds.map((id, index) => ({
    id,
    display_order: index,
  }));
  return bulkUpdateOptions(options);
};

/**
 * List plants for ALV provisioning flow.
 */
export const getPlantProvisionOptions = async (): Promise<PlantProvisionOption[]> => {
  try {
    const response = await apiClient.get('/plants/', { params: { page_size: 200 } });
    const data = response.data?.results || response.data || [];
    return data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Provision ALV mapping to existing/new plant and optionally upload layout image.
 */
export const provisionAlvPlant = async (
  payload: ProvisionAlvPlantPayload
): Promise<ProvisionAlvPlantResult> => {
  try {
    const formData = new FormData();
    formData.append('alv_id', payload.alv_id);
    formData.append('mode', payload.mode);

    if (payload.existing_plant_id !== undefined) formData.append('existing_plant_id', String(payload.existing_plant_id));
    if (payload.plant_name) formData.append('plant_name', payload.plant_name);
    if (payload.plant_code) formData.append('plant_code', payload.plant_code);
    if (payload.location) formData.append('location', payload.location);
    if (payload.country) formData.append('country', payload.country);
    if (payload.layout_name) formData.append('layout_name', payload.layout_name);
    if (payload.width !== undefined) formData.append('width', String(payload.width));
    if (payload.height !== undefined) formData.append('height', String(payload.height));

    formData.append('replace_existing_layout', String(!!payload.replace_existing_layout));
    formData.append('force_reassign_alv', String(!!payload.force_reassign_alv));

    if (payload.background_image_file) {
      formData.append('background_image_file', payload.background_image_file);
    }

    const response = await apiClient.post('/workshop-options/provision-alv-plant/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * List departments with linked plants for settings management.
 */
export const getDepartmentManagementOptions = async (): Promise<DepartmentManagementOption[]> => {
  try {
    const response = await apiClient.get('/departments/management/');
    return response.data?.results || response.data || [];
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Create department with primary plant + linked plants.
 */
export const createDepartmentManagementOption = async (
  payload: DepartmentManagementPayload
): Promise<DepartmentManagementOption> => {
  try {
    const response = await apiClient.post('/departments/', payload);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Update department with primary plant + linked plants.
 */
export const updateDepartmentManagementOption = async (
  id: number,
  payload: Partial<DepartmentManagementPayload>
): Promise<DepartmentManagementOption> => {
  try {
    const response = await apiClient.patch(`/departments/${id}/`, payload);
    return response.data;
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

/**
 * Delete department row.
 */
export const deleteDepartmentManagementOption = async (id: number): Promise<void> => {
  try {
    await apiClient.delete(`/departments/${id}/`);
  } catch (error) {
    throw new Error(handleApiError(error as AxiosError));
  }
};

export default {
  getAllOptions,
  getOptionsByAttribute,
  getGroupedOptions,
  getAttributes,
  getOption,
  createOption,
  updateOption,
  deleteOption,
  bulkUpdateOptions,
  toggleOptionActive,
  reorderOptions,
  getPlantProvisionOptions,
  provisionAlvPlant,
  getDepartmentManagementOptions,
  createDepartmentManagementOption,
  updateDepartmentManagementOption,
  deleteDepartmentManagementOption,
};
